import PropTypes from 'prop-types';
import { SMALL } from '@/config/assets';
import SquareImage from '@/components/component-library/Image/KeakieImageSquare';
import OptionTripleDotIcon from '@/components/component-library/Icon/types/OptionTripleDot';
import GripLinesIcon from '@/components/component-library/Icon/types/GripLines';
import ExplicitIcon from '@/components/component-library/Icon/types/Explicit';
import PauseRoundedIcon from '@/components/component-library/Icon/types/PauseRounded';
import PlayRoundedIcon from '@/components/component-library/Icon/types/PlayRounded';
import Text, { FONT_SIZE_040 } from '@/components/component-library/Text';
import { classes } from '@/helpers/styling';
import { getTimeFromSeconds } from '@/helpers/math';
import { isEpisodeNewlyReleased, isShowNewlyReleased } from '@/helpers/common';
import useShouldRenderForMobile from '@/hooks/layout/useShouldRenderForMobile';
import Span from '@/components/component-library/Span';
import BadgeNew from '@/components/component-library/BadgeNew';

const EpisodePreviewWithImage = ({
  episode,
  episodeKey,
  onClickMenu,
  onClick,
  isPlaying,
  loading,
  className,
  elementIdString,
  fixedFontSize,
  useGripHandle,
  showDescription,
  showDuration,
  alignContentTop,
  showPlayPauseIcon,
  isPaused,
  extraPadding
}) => {
  const {
    id,
    description,
    show,
    release_date,
    title,
    is_explicit,
    duration
  } = episode || {};
  const {
    title: channelTitle, release_date: channelReleaseDate, assets
  } = show || {};

  const shouldRenderForMobile = useShouldRenderForMobile();
  const isNewShow = isShowNewlyReleased({ releaseDate: channelReleaseDate });

  const isNew = (!isNewShow || shouldRenderForMobile) && isEpisodeNewlyReleased({ releaseDate: release_date });
  const isExplicit = !!is_explicit;

  const elementId = `episode-preview-${ episodeKey || id }-${ elementIdString }`;

  const handleClickMenu = (e) => {
    onClickMenu(e, episode, elementId, episodeKey);
  };

  const handleClick = onClick
    ? () => onClick(id, episode, episodeKey)
    : undefined;

  return (
    <div
      className={ classes('episode-preview-with-artwork', {
        '--is-playing': isPlaying,
        '--extra-padding': extraPadding,
        '--is-loading': loading
      }, className) }
      onClick={ loading ? undefined : handleClick }
      onContextMenu={ loading ? undefined : handleClickMenu }
    >
      { useGripHandle && !loading && (
        <div className='episode-grip-handle'>
          <GripLinesIcon />
        </div>
      ) }
      <div className='episode-preview-artwork-wrapper'>
        <div className='image-wrapper'>
          <SquareImage
            assetSize={ SMALL }
            assetsObj={ assets }
            className='episode-preview-artwork'
            key={ id }
            loading={ loading }
          />
          { showPlayPauseIcon && (
            <>
              <div className='episode-image-mask' />
              <div className='episode-play-pause-icon'>
                { isPaused ? <PlayRoundedIcon /> : <PauseRoundedIcon /> }
              </div>
            </>
          ) }
        </div>
      </div>
      <div className={ classes('episode-preview-with-artwork-content', { '--align-content-top': alignContentTop }) }>
        <div className='info'>
          <Text
            bold
            className='episode-title'
            fixed={ fixedFontSize }
            loading={ loading } useHeaderFont
          >
            <Span className={ classes('title-text', { '--is-playing': isPlaying }) }>
              { title }
            </Span>
            { isNew && <BadgeNew className='new-badge' /> }
          </Text>
          <Text
            className='description-text'
            fixed={ fixedFontSize }
            loading={ loading }
          >
            { showDescription ? <Span className='episode-description'>{ description }</Span> : <Span className='channel-title'>{ channelTitle }</Span> }
          </Text>
          { showDuration && (
            <div className='bottom-info-wrapper'>
              <Text
                className='episode-duration'
                loading={ loading }
                size={ FONT_SIZE_040 }
              >
                { getTimeFromSeconds(duration).timeString }
              </Text>
              { isExplicit && <div className='explicit-square'><ExplicitIcon /></div> }
            </div>
          ) }
        </div>
        { onClickMenu && !loading && (
          <div
            className={ classes('menu-button', { '--extra-padding': extraPadding }) }
            data-type='menu-button'
            id={ elementId }
            onClick={ handleClickMenu }
          >
            <OptionTripleDotIcon
              data-type='menu-button'
            />
          </div>
        ) }
      </div>
    </div>
  );
};

EpisodePreviewWithImage.propTypes = {
  episode: PropTypes.object,
  episodeKey: PropTypes.string,
  onClickMenu: PropTypes.func,
  onClick: PropTypes.func,
  isPlaying: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
  elementIdString: PropTypes.string,
  fixedFontSize: PropTypes.bool,
  useGripHandle: PropTypes.bool,
  showDescription: PropTypes.bool,
  showDuration: PropTypes.bool,
  alignContentTop: PropTypes.bool,
  showPlayPauseIcon: PropTypes.bool,
  isPaused: PropTypes.bool,
  extraPadding: PropTypes.bool
};

EpisodePreviewWithImage.defaultProps = {
  episode: null,
  episodeKey: null,
  onClickMenu: undefined,
  onClick: undefined,
  isPlaying: false,
  loading: false,
  className: '',
  elementIdString: null,
  fixedFontSize: false,
  useGripHandle: false,
  showDescription: false,
  showDuration: false,
  alignContentTop: false,
  showPlayPauseIcon: false,
  isPaused: true,
  extraPadding: false
};

export default EpisodePreviewWithImage;